import { CheckCircleIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import React, { useState } from "react";
import {
  AutopilotSearchDesignInitializationStep_JobFragment,
  AutopilotSearchDesignStep,
} from "../../../__generated__/graphql";
import { gql } from "@apollo/client";
import CiroSpinner from "../../shared/CiroSpinner";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";

export const AutopilotSearchDesignInitializationStep_Job = gql`
  fragment AutopilotSearchDesignInitializationStep_Job on AutopilotSearchDesignJob {
    id
    name
  }
`;

interface IJobGroup {
  job: AutopilotSearchDesignInitializationStep_JobFragment;
  steps: AutopilotSearchDesignStep[];
  allJobsAreComplete: boolean;
  isBestStrategy?: boolean;
  isSelected?: boolean;
  onSelect?: (jobId: string) => void;
  children: React.ReactNode;
}

const CuratedSearchDesignJobGroup = ({
  job,
  steps,
  isSelected = false,
  onSelect,
  allJobsAreComplete,
  isBestStrategy,
  children,
}: IJobGroup) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const jobSteps = steps.filter((step) => {
    if ("jobId" in step) {
      return step.jobId === job.id;
    }
    return false;
  });
  
  // Check if there's only one job by looking for initialization steps
  const initStep = steps.find(step => step.__typename === "AutopilotSearchDesignInitializationStep");
  const jobCount = initStep && "initializationData" in initStep ? 
    initStep.initializationData?.jobs?.length || 0 : 0;

  const jobHasResults = jobSteps.some(
    (step) => step.__typename === "AutopilotSearchDesignInternalFiltersStep",
  );
  const isRunning = !allJobsAreComplete;

  const getLatestMessage = () => {
    if (jobSteps.length === 0) return "";

    const lastTextStep = [...jobSteps]
      .reverse()
      .find((step) => step.__typename === "AutopilotSearchDesignTextOnlyStep");

    if (lastTextStep && "textOnlyData" in lastTextStep) {
      return lastTextStep.textOnlyData?.text || "";
    }

    return "";
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div
      className={classNames(
        "mb-6",
        "border",
        isSelected ? "border-orange-500" : "border-gray-200",
        "rounded-md",
        "overflow-hidden",
        isSelected && "shadow-lg shadow-orange-100",
      )}
    >
      <div
        onClick={toggleCollapse}
        className={classNames(
          "px-4",
          "py-3",
          "border-b",
          "w-full",
          isSelected ? "border-orange-300" : "border-gray-200",
          isSelected ? "bg-orange-50" : "bg-gray-50",
          "cursor-pointer",
          "transition-colors",
          isSelected ? "hover:bg-orange-100" : "hover:bg-gray-100",
          "flex",
          "flex-row",
          "items-center",
        )}
      >
        <div className={classNames("flex-1", "flex", "items-center", "gap-2")}>
          <ChevronRightIcon
            className={classNames(
              "w-5",
              "h-5",
              isSelected ? "text-orange-500" : "text-gray-500",
              "transition-transform duration-200",
              !isCollapsed && "rotate-90",
            )}
          />

          <div className={classNames("flex", "flex-row", "gap-1")}>
            <div className={classNames("text-sm")}>
              {jobCount > 1 ? "Testing search:" : "Preparing search:"}
            </div>
            <div className={classNames("font-medium", "text-sm")}>
              {job.name}
            </div>

            {isBestStrategy && (
              <span
                className={classNames(
                  "text-sm",
                  "ml-1",
                  "text-orange-600",
                  "flex",
                  "items-center",
                )}
              >
                <CheckCircleIcon
                  className={classNames(
                    "w-4",
                    "h-4",
                    "mr-1",
                    "text-orange-600",
                  )}
                />
                Recommended
              </span>
            )}
          </div>

          {isRunning && (
            <div className="ml-2">
              <CiroSpinner loading useBeatLoader />
            </div>
          )}

          {jobHasResults && !isBestStrategy && (
            <CheckCircleIcon
              className={classNames("w-5", "h-5", "text-neutral-500", "ml-2")}
            />
          )}
        </div>

        <div className="flex items-center gap-2">
          {isCollapsed && !isSelected && getLatestMessage() && (
            <div
              className={classNames(
                "text-xs",
                "text-gray-500",
                "truncate",
                "max-w-[200px]",
              )}
            >
              {getLatestMessage()}
            </div>
          )}

          {allJobsAreComplete &&
            jobHasResults &&
            (isSelected ? (
              <div
                className={classNames(
                  "flex",
                  "items-center",
                  "text-xs",
                  "text-orange-600",
                  "font-medium",
                  "ml-1",
                  isBestStrategy ? "text-orange-600" : "text-gray-500",
                )}
              >
                <span className={classNames("flex", "items-center")}>
                  Viewing filters
                </span>
              </div>
            ) : (
              onSelect && (
                <CiroButton
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent toggling the collapse state
                    onSelect(job.id);
                  }}
                  style={CiroButtonStyleEnum.UNSTYLED}
                  customClassName={classNames(
                    "px-3",
                    "py-1",
                    "text-xs",
                    "rounded",
                    "border",
                    "bg-white",
                    "text-gray-700",
                    "border-gray-300",
                    "hover:bg-blue-50",
                    "transition-colors",
                    "font-medium",
                    "ml-1",
                  )}
                  analyticsField={"Toggle filters in search builder"}
                >
                  View filters
                </CiroButton>
              )
            ))}
        </div>
      </div>

      {!isCollapsed && (
        <div className={classNames("flex", "flex-col", "gap-2", "p-4")}>
          {children}
        </div>
      )}
    </div>
  );
};

export default CuratedSearchDesignJobGroup;
